import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import PageHeader from '../components/PageHeader';
import { ArrowRight } from 'lucide-react';

/**
 * Guide / mode d'emploi for the kitchen team.
 * Trilingual (FR default, NL, EN). The garde-manger is presented as the heart
 * of the tool: prices live there, and recipes draw on them.
 */

const T = {
    fr: {
        title: 'Comment ça marche',
        intro: "Costy calcule le coût et la marge de chaque plat à partir des prix de vos ingrédients. Vous réglez un prix une fois, et toutes les recettes qui l’utilisent suivent.",
        flow: ['Factures', 'Ingrédients (prix)', 'Plats (recettes)'],
        flowNote: "Les factures alimentent un catalogue brut. Les Ingrédients sont votre liste propre et maîtrisée, avec un prix par unité. Les plats piochent dedans.",
        sections: [
            {
                h: 'Commencez par les Ingrédients',
                p: "Un ingrédient ne sert dans les recettes que s’il a un prix réglé ici. Pas de prix = le plat affiche « coût incomplet ».",
                bullets: [
                    "Ajoutez un ingrédient, puis cherchez la bonne ligne de facture pour le lier.",
                    "Réglez le prix par unité. Si la facture donne un lot (ex. 18,48 € pour 12 canettes), entrez « 18,48 € pour 12 » : Costy calcule 1,54 €/pièce et garde le détail.",
                    "Le prix reste figé jusqu’à ce que vous le confirmiez à nouveau (bouton « Vérifier les prix récents »).",
                    "Choisissez la bonne unité : au kilo (kg), au litre (l) ou à la pièce.",
                ],
            },
            {
                h: 'Construire la recette d’un plat',
                p: "Ouvrez un plat, ajoutez les ingrédients depuis les Ingrédients (seuls les actifs apparaissent), avec leur quantité.",
                bullets: [
                    "Entrez la quantité pour TOUTE la recette (le batch complet), pas pour une part.",
                    "Utilisez une unité cohérente avec l’ingrédient : grammes pour un prix au kg, millilitres pour un prix au litre, pièce pour les œufs/fruits comptés.",
                    "Le coût de chaque ligne se calcule tout seul à partir du prix dans les Ingrédients.",
                ],
            },
            {
                h: 'Les préparations',
                p: "Une crème, une sauce ou une pâte qui sert dans plusieurs plats : créez-la une fois dans Préparations, avec sa recette et ce qu’elle donne (ex. 1,8 kg ou 10 pièces).",
                bullets: [
                    "Son prix se calcule tout seul et suit le prix de ses ingrédients.",
                    "Dans un plat, ajoutez-la comme un ingrédient, avec la quantité servie (ex. 60 g).",
                ],
            },
            {
                h: 'Le nombre de portions',
                p: "Vous saisissez la recette complète (tout le batch). Le champ « Portions par recette » dit combien de parts vendues ce batch produit.",
                bullets: [
                    "Coût par portion = coût total des ingrédients ÷ nombre de portions.",
                    "Exemple : un gâteau pour 32 tranches → mettez 32. 30 cookies → mettez 30.",
                    "C’est ce coût par portion qui est comparé au prix de vente pour la marge.",
                ],
            },
            {
                h: 'Vérifier un plat existant',
                p: "Cliquez sur le nom d’un plat pour voir le détail du coût. C’est la façon de contrôler le travail.",
                bullets: [
                    "Une étiquette rouge « coût incomplet » = un ingrédient n’a pas de prix. Corrigez-le dans les Ingrédients.",
                    "Sous chaque ingrédient, la ligne « réf. » montre le produit facturé d’origine et son prix brut.",
                    "Vérifiez que les quantités et les unités ont du sens, et que le coût par portion et la marge sont réalistes.",
                ],
            },
            {
                h: 'Prêt-à-vendre',
                p: "Pour un article acheté et revendu tel quel (ex. une canette de cola), marquez-le « prêt-à-vendre » dans les Ingrédients, puis liez-le 1:1 dans un plat.",
                bullets: [
                    "Les Ingrédients portent le prix unitaire ; le plat n’a qu’une seule ligne, en quantité 1.",
                ],
            },
            {
                h: 'Les cartes en haut des Plats',
                p: "En haut de la page Plats, quatre cartes donnent l’état de la carte d’un coup d’œil.",
                bullets: [
                    "Sans recette : plats à compléter. Prix à définir : recette faite, prix de vente manquant. Coût incomplet : recette avec un prix d’achat manquant.",
                    "Cliquez sur une carte pour filtrer la liste et avancer plat par plat.",
                ],
            },
        ],
        langName: 'Français',
    },
    nl: {
        title: 'Hoe het werkt',
        intro: "Costy berekent de kostprijs en marge van elk gerecht uit de prijzen van je ingrediënten. Je stelt een prijs één keer in, en alle recepten die hem gebruiken volgen.",
        flow: ['Facturen', 'Voorraadkast (prijzen)', 'Gerechten (recepten)'],
        flowNote: "Facturen vullen een ruwe catalogus. Ingrédients is jouw nette, beheerde lijst met een prijs per eenheid. Gerechten putten daaruit.",
        sections: [
            {
                h: 'Begin bij Ingrédients',
                p: "Een ingrediënt telt pas mee in recepten als het hier een prijs heeft. Geen prijs = het gerecht toont « onvolledige kost ».",
                bullets: [
                    "Voeg een ingrediënt toe en zoek de juiste factuurregel om het te koppelen.",
                    "Stel de prijs per eenheid in. Geeft de factuur een verpakking (bv. € 18,48 voor 12 blikjes), vul dan « 18,48 € voor 12 » in: Costy rekent 1,54 €/stuk en bewaart de berekening.",
                    "De prijs blijft vast tot je hem opnieuw bevestigt (knop « Recente prijzen controleren »).",
                    "Kies de juiste eenheid: per kilo (kg), per liter (l) of per stuk.",
                ],
            },
            {
                h: 'Een recept opbouwen',
                p: "Open een gerecht en voeg ingrediënten toe uit Ingrédients (enkel de actieve verschijnen), met hun hoeveelheid.",
                bullets: [
                    "Geef de hoeveelheid voor het HELE recept (de volledige batch), niet per portie.",
                    "Gebruik een eenheid die past bij het ingrediënt: gram bij een kiloprijs, milliliter bij een literprijs, stuk voor geteld fruit/eieren.",
                    "De kost per regel wordt automatisch berekend uit de prijs in Ingrédients.",
                ],
            },
            {
                h: 'Bereidingen',
                p: "Een crème, saus of beslag dat in meerdere gerechten zit: maak het één keer aan in Préparations, met het recept en wat het oplevert (bv. 1,8 kg of 10 stuks).",
                bullets: [
                    "De prijs wordt automatisch berekend en volgt de prijs van de ingrediënten.",
                    "Voeg het in een gerecht toe zoals een ingrediënt, met de geserveerde hoeveelheid (bv. 60 g).",
                ],
            },
            {
                h: 'Het aantal porties',
                p: "Je voert het volledige recept in (de hele batch). Het veld « Porties per recept » zegt hoeveel verkochte porties die batch oplevert.",
                bullets: [
                    "Kost per portie = totale ingrediëntkost ÷ aantal porties.",
                    "Voorbeeld: een taart voor 32 sneden → zet 32. 30 koekjes → zet 30.",
                    "Die kost per portie wordt vergeleken met de verkoopprijs voor de marge.",
                ],
            },
            {
                h: 'Een bestaand gerecht controleren',
                p: "Klik op de naam van een gerecht om de kostendetail te zien. Zo controleer je het werk.",
                bullets: [
                    "Een rood label « onvolledige kost » = een ingrediënt heeft geen prijs. Corrigeer het in Ingrédients.",
                    "Onder elk ingrediënt toont de regel « réf. » het oorspronkelijke gefactureerde product en zijn ruwe prijs.",
                    "Controleer of hoeveelheden en eenheden kloppen en of de kost per portie en de marge realistisch zijn.",
                ],
            },
            {
                h: 'Klaar voor verkoop',
                p: "Voor een artikel dat je koopt en zoals het is doorverkoopt (bv. een blikje cola), markeer het als « klaar voor verkoop » in Ingrédients en koppel het 1:1 in een gerecht.",
                bullets: [
                    "Ingrédients draagt de stukprijs; het gerecht heeft maar één regel, hoeveelheid 1.",
                ],
            },
            {
                h: 'De kaarten bovenaan Gerechten',
                p: "Bovenaan de pagina Gerechten geven vier kaarten in één oogopslag de stand van de kaart.",
                bullets: [
                    "Zonder recept: aan te vullen gerechten. Prijs in te vullen: recept klaar, verkoopprijs ontbreekt. Onvolledige kost: recept met een ontbrekende aankoopprijs.",
                    "Klik op een kaart om de lijst te filteren en gerecht per gerecht te werken.",
                ],
            },
        ],
        langName: 'Nederlands',
    },
    en: {
        title: 'How it works',
        intro: "Costy works out the cost and margin of each dish from your ingredient prices. Set a price once, and every recipe using it follows.",
        flow: ['Invoices', 'Ingrédients (prices)', 'Dishes (recipes)'],
        flowNote: "Invoices feed a raw catalogue. Ingrédients is your clean, curated list with a price per unit. Dishes draw from it.",
        sections: [
            {
                h: 'Start with Ingrédients',
                p: "An ingredient only counts in recipes once it has a price set here. No price = the dish shows “incomplete cost”.",
                bullets: [
                    "Add an ingredient, then search for the right invoice line to link it.",
                    "Set the price per unit. If the invoice gives a pack (e.g. €18.48 for 12 cans), enter “€18.48 for 12”: Costy works out €1.54/each and keeps the breakdown.",
                    "The price stays fixed until you confirm it again (the “Check recent prices” button).",
                    "Pick the right unit: per kilo (kg), per litre (l), or per piece.",
                ],
            },
            {
                h: 'Building a dish’s recipe',
                p: "Open a dish and add ingredients from Ingrédients (only active ones show up), each with its quantity.",
                bullets: [
                    "Enter the quantity for the WHOLE recipe (the full batch), not for one portion.",
                    "Use a unit that matches the ingredient: grams for a per-kg price, millilitres for a per-litre price, piece for counted eggs/fruit.",
                    "Each line’s cost is computed automatically from the Ingrédients price.",
                ],
            },
            {
                h: 'Preparations',
                p: "A cream, sauce or batter used in several dishes: create it once under Préparations, with its recipe and what it yields (e.g. 1.8 kg or 10 pieces).",
                bullets: [
                    "Its price is worked out automatically and follows its ingredients’ prices.",
                    "In a dish, add it like an ingredient, with the amount served (e.g. 60 g).",
                ],
            },
            {
                h: 'The number of portions',
                p: "You enter the full recipe (the whole batch). The “Portions per recipe” field says how many sold portions that batch yields.",
                bullets: [
                    "Cost per portion = total ingredient cost ÷ number of portions.",
                    "Example: a cake for 32 slices → set 32. 30 cookies → set 30.",
                    "That cost per portion is what’s compared to the selling price for the margin.",
                ],
            },
            {
                h: 'Verifying an existing dish',
                p: "Click a dish name to see the cost breakdown. That’s how you check the work.",
                bullets: [
                    "A red “incomplete cost” tag = an ingredient has no price. Fix it in Ingrédients.",
                    "Under each ingredient, the “réf.” line shows the original invoiced product and its raw price.",
                    "Check that quantities and units make sense, and that the cost per portion and margin look realistic.",
                ],
            },
            {
                h: 'Ready-to-sell',
                p: "For an item bought and resold as-is (e.g. a can of cola), flag it “ready-to-sell” in Ingrédients, then link it 1:1 in a dish.",
                bullets: [
                    "Ingrédients carries the unit price; the dish has a single line, quantity 1.",
                ],
            },
            {
                h: 'The cards atop Dishes',
                p: "At the top of the Dishes page, four cards show the state of the menu at a glance.",
                bullets: [
                    "No recipe: dishes to complete. Price to set: recipe done, selling price missing. Incomplete cost: recipe with a missing purchase price.",
                    "Click a card to filter the list and work through it dish by dish.",
                ],
            },
        ],
        langName: 'English',
    },
};

const LANGS = [['fr', 'FR'], ['nl', 'NL'], ['en', 'EN']];

const Guide = () => {
    useEffect(() => { document.title = 'Guide | Costy'; }, []);
    const [lang, setLang] = useState(() => localStorage.getItem('guideLang') || 'fr');
    const t = T[lang];

    const pick = (l) => { setLang(l); localStorage.setItem('guideLang', l); };

    return (
        <Layout>
            <div className="space-y-6">
                <PageHeader title={t.title}>
                    <div className="flex gap-1 rounded-full bg-muted p-1 shrink-0">
                        {LANGS.map(([code, label]) => (
                            <button
                                key={code}
                                onClick={() => pick(code)}
                                aria-pressed={lang === code}
                                className={`min-h-[36px] px-3 text-sm font-semibold rounded-full transition ${lang === code ? 'bg-mustard text-foreground' : ''}`}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                </PageHeader>

                <p className="max-w-[70ch] text-base leading-relaxed">{t.intro}</p>

                {/* Flow strip */}
                <div className="panel p-4">
                    <div className="flex flex-wrap items-center gap-2">
                        {t.flow.map((step, i) => (
                            <React.Fragment key={step}>
                                <span className={`text-xs font-semibold px-3 py-1.5 rounded-full ${i === 1 ? 'bg-mustard text-foreground' : 'bg-muted text-foreground'}`}>{step}</span>
                                {i < t.flow.length - 1 && <ArrowRight className="h-4 w-4 text-muted-foreground" />}
                            </React.Fragment>
                        ))}
                    </div>
                    <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{t.flowNote}</p>
                </div>

                {/* Sections */}
                <div className="space-y-4">
                    {t.sections.map((s, i) => (
                        <section key={i} className="panel p-5">
                            <h2 className="text-xl text-foreground leading-tight mb-2">{s.h}</h2>
                            <p className="text-sm text-foreground/90 leading-relaxed mb-2">{s.p}</p>
                            <ul className="space-y-1.5">
                                {s.bullets.map((b, j) => (
                                    <li key={j} className="flex gap-2 text-sm text-muted-foreground leading-relaxed">
                                        <span className="text-warn mt-0.5 shrink-0">•</span>
                                        <span>{b}</span>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    ))}
                </div>
            </div>
        </Layout>
    );
};

export default Guide;
